import { ArrowSmallRightIcon } from '@heroicons/react/24/solid'
import { ColumnDef } from '@tanstack/react-table'
import classNames from 'classnames'
import { WarningCircle } from 'iconoir-react'
import { compact, debounce, extend, uniqBy } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useState } from 'react'
import Button from '../Button'
import CircularProgressBar from '../CircularProgressBar'
import { ControlledSearch } from 'app/components/ui/controlled-search'
import { DateComponent } from '../DateComponent'
import { LazyLocationCell } from '../LazyLocationCell'
import PhoneNumber from '../PhoneNumber'
import { ContactCell } from '../shared/ContactCell'
import Spinner from '../Spinner'
import { Toggle } from '../Toggle'
import { ContactNameIcon } from './ContactNameIcon'
import PatientNameIcon from './PatientNameIcon'
import SimpleTable from 'app/components/table/SimpleTable'
import useFakeProgress from 'app/hooks/fakeProgress'
import type { ContactJson } from 'app/types/entities'
import type { PatientJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { useContactStore } from 'app/stores/StoreContext'
import { useUser, useWorkspace } from 'app/utils'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'

type Props = {
  children: React.ReactNode
  contact: ContactJson
  patient?: PatientJson
  onClose?: () => void
  className?: string
  title?: string
}

export default function ContactSearchModalLink({
  children,
  contact,
  patient,
  onClose,
  className,
  title = `Add Linked Patient`,
  requireLinkedPatient,
}: Props & { requireLinkedPatient?: boolean }) {
  const openItPlease = (evt: React.MouseEvent<HTMLDivElement>) => {
    evt.preventDefault()
    evt.stopPropagation()
    debug(`modal`, `openItPlease`)
    showModal(
      <ContactSearch
        locationId={contact?.locationId}
        contact={contact}
        patient={patient}
        requireLinkedPatient={requireLinkedPatient}
        done={() => {
          onClose?.()
          closeModal()
        }}
      />,
      {
        title,
        isFullScreen: false,
        removePadding: true,
        is80Percent: true,
        useRadix: true,
      },
    )
  }

  return (
    <>
      <div className={classNames(className, `cursor-pointer`)} onClick={openItPlease}>
        {children}
      </div>
    </>
  )
}

function ContactSearch({
  locationId,
  contact,
  patient,
  done,
  requireLinkedPatient,
}: {
  locationId?: string
  contact: ContactJson
  patient?: PatientJson
  done: () => void
  requireLinkedPatient?: boolean
}) {
  const contactStore = useContactStore()
  const [contacts, setContacts] = useState<ContactJson[]>()
  const [topMatches, setTopMatches] = useState<ContactJson[]>([])
  const workspace = useWorkspace()
  const [page, setPage] = useState<number>(0)
  const [matchedContact, setMatchedContact] = useState<ContactJson | null>(null)
  const [selectedContact, setSelectedContact] = useState<ContactJson | null>(null)
  const [showConfirmationStep, setShowConfirmationStep] = useState(false)
  const [inProgress, setInProgress] = useState(false)
  const [progress, progressStart, progressEnd] = useFakeProgress()
  const [searchWorkspace, setSearchWorkspace] = useState(false)
  const [searchValue, setSearchValue] = useState(``)
  const [isLoading, setIsLoading] = useState(false)

  const user = useUser()

  useEffect(() => {
    if (patient) {
      setMatchedContact(contact)
    }
  }, [patient])

  useEffect(() => {
    if (inProgress) {
      progressStart()
    }
  }, [inProgress])

  const defaultColumns = (
    selectedContact: ContactJson | null,
    matchedContact: ContactJson | null,
    onContactHandler: (contact: ContactJson) => void,
    onSelectContact: (contact: ContactJson) => void,
  ): ColumnDef<ContactJson & { matchTypes?: string[] }, any>[] =>
    compact([
      {
        accessorFn: (row) => row.id,
        id: `actions`,
        header: ``,
        maxSize: 50,
        enableResizing: false,
        cell: function Actions({ row }) {
          useEffect(() => {
            if (matchedContact?.id === row.original.id || contact?.id === row.original.id) {
              row.pin(`top`)
            }
            return () => row.pin(false)
          }, [])
          return (
            <div className="flex items-center justify-center">
              <input
                type="radio"
                checked={
                  selectedContact?.id === row.original.id || (!selectedContact && contact.id === row.original.id)
                }
                onChange={() => onSelectContact(row.original)}
                // disabled={matchedContact?.id === row.original.id ? true : undefined}
                className="h-4 w-4 border-strong text-blue-600 focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          )
        },
        meta: {
          defaultVisible: true,
          pinned: false,
          tooltipClassName: `w-full`,
        },
        enableSorting: false,
        enableHiding: false,
        size: 50,
        minSize: 50,
      },
      // user.type === `super` && {
      //   id: `id`,
      //   accessorKey: `id`,
      //   header: `ID`,
      //   meta: { group: `General`, defaultVisible: true, pinned: false, sortKey: `id.keyword` },
      // },
      {
        id: `name`,
        accessorFn: (row) => row.name,
        header: `Name`,
        enableResizing: true,
        meta: {
          group: `General`,
          defaultVisible: true,
          pinned: false,
          sortKey: `name.plain`,
          onClick: (row) => onContactHandler(row),
          tooltipText: (row) => row.name ?? ``,
        },
        enableHiding: false,
        minSize: 100,
        cell: (props) => (
          <span onClick={() => onSelectContact(props.row.original)}>
            <ContactCell contact={props.row.original} matchedContact={matchedContact} />
          </span>
        ),
      },
      {
        id: `Patient Linked`,
        accessorKey: `patientIds`,
        header: `Patient Linked`,
        meta: {
          defaultVisible: true,
          sortKey: `patientIds`,
        },
        cell: (props) => (
          <span
            className={classNames(
              `flex h-6 items-center rounded border px-1.5 text-xs font-medium not-italic leading-4`,
              props.getValue() ? `bg-green-100 text-green-700` : `bg-red-100 text-red-700`,
            )}
          >
            {props.getValue() ? `Linked` : `Not Linked`}
          </span>
        ),
      },
      {
        id: `locationId`,
        accessorKey: `locationId`,
        header: `Location`,
        cell: ({ row }) => <LazyLocationCell locationId={row.original.locationId!} />,
        meta: {
          defaultVisible: true,
        },
        enableSorting: false,
      },
      user?.type === `super` && {
        id: `matchTypes`,
        accessorKey: `matchTypes`,
        header: `Match Types`,
        meta: { group: `General`, defaultVisible: true, pinned: false, sortKey: `matchTypes.keyword` },
      },
      {
        id: `homePhone`,
        accessorKey: `homePhone`,
        header: `Home Phone`,
        meta: {
          group: `Communication`,
          defaultVisible: true,
          pinned: false,
          sortKey: `homePhone.keyword`,
        },
        cell: (props) => <PhoneNumber number={props.row.original.homePhone} />,
      },
      {
        id: `cellPhone`,
        accessorKey: `cellPhone`,
        header: `Cell Phone`,
        cell: (props) => <PhoneNumber number={props.row.original.cellPhone} />,
        meta: {
          group: `Communication`,
          defaultVisible: true,
          pinned: false,
          sortKey: `cellPhone.keyword`,
        },
      },
      {
        id: `firstAppointmentDate`,
        accessorKey: `firstAppointmentDate`,
        header: `First Appointment Date`,
        cell: (props) => <DateComponent value={props.row.original.firstAppointmentDate} dateOnly />,
        meta: {
          group: `PMS`,
          defaultVisible: true,
          sortKey: `firstAppointmentDate`,
        },
      },
      {
        id: `lastAppointmentDate`,
        accessorKey: `lastAppointmentDate`,
        header: `Last Appointment Date`,
        cell: (props) => <DateComponent value={props.row.original.lastAppointmentDate} dateOnly />,
        meta: {
          group: `PMS`,
          defaultVisible: true,
          sortKey: `lastAppointmentDate`,
        },
      },
    ])

  const data = useMemo(
    () =>
      searchValue !== ``
        ? uniqBy(compact([matchedContact, ...(contacts || [])]), `id`)
        : uniqBy(compact([...topMatches]), `id`),
    [searchValue, contacts, topMatches, matchedContact],
  )

  useEffect(() => {
    if (searchValue !== ``) {
      bg(async () => {
        const searchTerms: string[] = [searchValue]
        if (searchWorkspace) searchTerms.push(`workspaceId:${contact?.workspaceId}`)
        else searchTerms.push(`locationIds:${contact?.locationId}`)
        if (requireLinkedPatient) searchTerms.push(`patientIds:*`)
        const resp = await gqlFetchList<ContactJson>('contacts', {
          search: searchTerms.join(' '),
          pagination: { page, limit: 25 },
        })
        if (resp?.hits) {
          setContacts((data) =>
            uniqBy([...(data || []), ...resp.hits], `id`).filter((c) => c.id !== matchedContact?.id),
          )
        }
        setIsLoading(false)
      })
    } else {
      setIsLoading(false)
    }
  }, [searchValue, searchWorkspace, page])

  // fetch the top matches on load
  useEffect(() => {
    if (contact) {
      setIsLoading(true)
      let additionalSearchesString = JSON.stringify({
        firstAppointmentMadeDate:
          contact.firstAppointmentMadeDate && moment(contact.firstAppointmentMadeDate).format(`YYYY-MM-DD`),
        patientCreatedDate: contact?.patientCreatedDate && moment(contact?.patientCreatedDate).format(`YYYY-MM-DD`),
        firstAppointmentDate: contact.firstAppointmentDate && moment(contact.firstAppointmentDate).format(`YYYY-MM-DD`),
        birthDate: contact.birthDate && moment(contact.birthDate).format(`YYYY-MM-DD`),
      })
      const requireLinkedPatientParam = requireLinkedPatient ? `&requireLinkedPatient=true` : ``
      bg(async () => {
        let data = await window
          .fetch(
            `/api/contacts/matches?name=${contact.name}&locationId=${contact?.locationId}&phone=${
              contact.cellPhone || contact?.homePhone
            }&additionalSearches=${additionalSearchesString}&limit=5${requireLinkedPatientParam}`,
            { credentials: `include` },
          )
          .then((r) => r.json())
        let _topMatches = data?.map((m) => extend(m.contact, { matchTypes: m.matchTypes }))
        debug(`rematch`, `top matches`, data, _topMatches)

        setTopMatches(_topMatches)
        setIsLoading(false)
      })
    }
  }, [contact])

  const handleSelectContact = (selectedContact: ContactJson) => {
    if (selectedContact.id === contact?.id) {
      setSelectedContact(null)
    } else {
      setSelectedContact(selectedContact)
    }
  }

  const columns = useMemo(
    () =>
      defaultColumns(
        selectedContact,
        matchedContact,
        (contact) => {
          const url = `/contacts?tab=all&drawer=${contact.id}&query=${contact.name}&wId=${workspace?.id ?? null}`
          window.open(url, `_blank`)
        },
        handleSelectContact,
      ),
    [selectedContact, matchedContact],
  )

  if (!user) return null

  const mergeContacts = async () => {
    setInProgress(true)
    await contactStore.mergeContacts(contact?.id!, selectedContact?.id!).then(() => {
      debug(`patients`, `done merging`)
      progressEnd()
    })
  }

  const goToContact = () => {
    done()
    debug(`rematch`, `reloading`)
    window.location.reload()
    return
  }

  const onSearchValueChange = debounce((val) => {
    setSearchValue(val)
    setPage(0)
    setContacts(undefined)
    setIsLoading(true)
  }, 250)

  const onSearchWorkspaceToggle = (toggle: boolean) => {
    setSearchWorkspace(toggle)
    setPage(0)
    setContacts(undefined)
    setIsLoading(true)
  }

  return (
    <div id="patient-search-modal-link" className="flex h-full w-full flex-col">
      <div className="flex flex-col gap-6 bg-surface-subtle px-4 pb-8 pt-12 md:flex-row md:items-center md:justify-between md:gap-0 md:px-8">
        <div className="relative grow space-y-4 rounded-lg border border-subtle bg-surface p-4 shadow-elevation-2">
          <div className="flex items-center space-x-2">
            <ContactNameIcon contact={contact} heightClassName={`h-10`} widthClassName={`w-10`} />
            <div>
              <p className="text-heading-sm text-strong">{contact?.name}</p>
              <p className="text-body-xs text-subtle">
                <LazyLocationCell locationId={contact?.locationId!} />
              </p>
              <div className="text-body-xs text-subtle">
                <PhoneNumber number={contact?.cellPhone ?? contact?.homePhone} />
              </div>
            </div>
          </div>
          <p className="absolute -top-10 left-0 mb-2 text-ui-sm">Link Contact</p>
        </div>
        <div className="mx-auto flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-default bg-surface shadow-elevation-1 md:mx-8">
          <ArrowSmallRightIcon className="h-4 w-4 rotate-90 text-subtle md:rotate-0" />
        </div>
        <div className="relative h-full grow space-y-4 rounded-lg border border-subtle bg-surface p-4 shadow-elevation-2">
          <div className="flex items-center space-x-2">
            {selectedContact ? (
              <>
                <ContactNameIcon contact={selectedContact} heightClassName={`h-10`} widthClassName={`w-10`} />
                <div>
                  <p className="text-heading-sm text-strong">{selectedContact?.name}</p>
                  <p className="text-body-xs text-subtle">
                    <LazyLocationCell locationId={selectedContact?.locationId!} />
                  </p>
                  <div className="text-body-xs text-subtle">
                    <PhoneNumber number={selectedContact?.cellPhone ?? selectedContact?.homePhone} />
                  </div>
                </div>
              </>
            ) : patient ? (
              <>
                <PatientNameIcon patient={patient} heightClassName={`h-10`} widthClassname={`w-10`} />
                <div>
                  <p className="text-heading-sm text-strong">{`${patient.firstName} ${patient.lastName}`}</p>
                  <p className="text-body-xs text-subtle">
                    <LazyLocationCell locationId={patient?.locationId!} />
                  </p>
                  <div className="text-body-xs text-subtle">
                    <PhoneNumber number={patient?.cellPhone ?? patient?.homePhone} />
                  </div>
                </div>
              </>
            ) : (
              <>
                <WarningCircle className="h-5 w-5 text-subtle" />
                <p className="h-10 text-body-xs leading-10 text-minimal">No contact selected</p>
              </>
            )}
          </div>
          <p className="absolute -top-10 left-0 mb-2 text-ui-sm">With Contact</p>
        </div>
      </div>
      {!showConfirmationStep ? (
        <>
          <div className="bg-surface-subtle px-4 pb-6 pt-2">
            <div className="mx-4 flex flex-col gap-3 md:flex-row md:justify-between md:gap-2">
              <div>
                <ControlledSearch onValueChange={onSearchValueChange} />
              </div>
              <Toggle label="Search Workspace" defaultValue={searchWorkspace} handleToggle={onSearchWorkspaceToggle} />
            </div>
          </div>
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size={20} />
            </div>
          ) : (
            <div className="flex flex-col overflow-hidden">
              <SimpleTable
                data={data}
                columns={columns}
                onScroll={debounce((e) => {
                  const target = e.target as HTMLDivElement
                  if (target.scrollTop > 0 && target.scrollTop + target.offsetHeight >= target.scrollHeight * 0.8) {
                    setPage((page) => page + 1)
                  }
                }, 1000)}
                hideFooter
              />
            </div>
          )}
        </>
      ) : !inProgress ? (
        <div className="flex grow items-center justify-center">
          <div className="p-4">
            <p className="text-body-md">
              <span className="text-heading-sm">{contact?.name}</span> will be matched with{` `}
              <span className="text-heading-sm">{selectedContact?.name}</span>
            </p>
            <div className="ml-2 mt-4 flex flex-col space-y-4 text-body-sm text-subtle">
              <p>1. The resulting contact will inherit its properties from the patient record.</p>
              <p>2. Using the new data and activities the contact will be rebuilt.</p>
              <p>3. This may result in a change in status, dates and a variety of existing contact fields.</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="mx-auto my-4 flex grow flex-col items-center justify-center space-y-4">
          <CircularProgressBar progress={progress} />
          <p className="text-heading-md">{progress !== 100 ? `Merging in progress...` : `Successfully merged`}</p>
        </div>
      )}
      <div className="mt-auto flex justify-end space-x-2 border-t border-default px-6 py-3">
        {!showConfirmationStep ? (
          <div className="flex grow items-center justify-between">
            <div></div>
            <div className="flex items-center space-x-2">
              <Button mode="secondary" size="md" onClick={() => done()} label="Cancel" />
              <Button
                label="Next"
                mode="primary"
                size="md"
                disabled={!selectedContact || selectedContact.id === contact.id ? true : undefined}
                onClick={() => setShowConfirmationStep(true)}
              />
            </div>
          </div>
        ) : !inProgress ? (
          <div className="flex grow items-center justify-between">
            <p className="text-heading-xs text-red-500">This action can't be undone</p>
            <div className="flex space-x-2">
              <Button
                type="button"
                mode="secondary"
                size="md"
                label="Back"
                onClick={() => setShowConfirmationStep(false)}
              />
              <Button mode="primary" size="md" label="Confirm" onClick={() => mergeContacts()} />
            </div>
          </div>
        ) : (
          <Button
            disabled={progress !== 100 ? true : undefined}
            className="btn btn-primary btn-md"
            onClick={() => goToContact()}
            label="Go to Contact"
            mode="primary"
            size="md"
          />
        )}
      </div>
    </div>
  )
}
